import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowsRotate,
  faCircleCheck,
  faCircleNodes,
  faDownload,
  faDesktop,
  faExclamationTriangle,
  faHeartPulse,
  faMobileScreenButton,
  faPaperPlane,
  faPlus,
  faShieldHalved,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { faFacebook, faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import { MetricCard } from "../../../components/MetricCard/MetricCard"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useWhatsAppAutomation } from "../../../hooks/WhatsAppAutomation/useWhatsAppAutomation"
import type {
  IWhatsAppAgentInstallerInfo,
  IWhatsAppAgentOverviewDto,
  IWhatsAppPairingCodeResult,
  WhatsAppAgentConnectionStatus,
} from "../../../Objects/WhatsAppAutomation/WhatsAppAgentOverview"
import { WhatsAppPairingPopup } from "./components/WhatsAppPairingPopup"
import { WhatsAppSendTestPopup } from "./components/WhatsAppSendTestPopup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"

const normalizeStatus = (status: WhatsAppAgentConnectionStatus): string => {
  if (typeof status === "number") {
    const byNumber: Record<number, string> = {
      0: "NotStarted",
      1: "WaitingQrCode",
      2: "Ready",
      3: "Disconnected",
      4: "Failed",
    }

    return byNumber[status] ?? "NotStarted"
  }

  return status
}

const buildDownloadUrl = (installerInfo: IWhatsAppAgentInstallerInfo | null): string => {
  const downloadUrl = installerInfo?.downloadUrl?.trim()

  if (!downloadUrl) {
    return API_ROUTES.WHATSAPP_AUTOMATION.DOWNLOAD_AGENT
  }

  return downloadUrl.startsWith("http") ? downloadUrl : `${API_ROUTES.URL_API}${downloadUrl}`
}

const formatHeartbeatAge = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) {
    return "-"
  }

  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)

  const remainingSeconds = seconds % 60

  return `${minutes}m ${remainingSeconds}s`
}

const isPairingCodeValid = (pairingData: IWhatsAppPairingCodeResult | null): boolean => {
  if (!pairingData?.pairingCode || !pairingData.expiresAt) {
    return false
  }

  const expirationTime = new Date(pairingData.expiresAt).getTime()

  return Number.isFinite(expirationTime) && expirationTime > Date.now()
}

export const WhatsAppAutomationPage: React.FC = () => {
  const { t } = useTranslation(["pages"])

  const { setNewConfirmation } = useConfirmation()

  const { getOverview, getInstallerInfo, generatePairingCode, sendMessage, deleteAgent } = useWhatsAppAutomation()

  const [agents, setAgents] = useState<IWhatsAppAgentOverviewDto[]>([])

  const [isOverviewLoaded, setIsOverviewLoaded] = useState(false)

  const [installerInfo, setInstallerInfo] = useState<IWhatsAppAgentInstallerInfo | null>(null)

  const [agentName, setAgentName] = useState(t("label_whatsapp_automation_default_agent_name"))

  const [pairingData, setPairingData] = useState<IWhatsAppPairingCodeResult | null>(null)

  const [isPairingPopupOpen, setIsPairingPopupOpen] = useState(false)

  const [isTestPopupOpen, setIsTestPopupOpen] = useState(false)

  const [toPhone, setToPhone] = useState("")

  const [messageBody, setMessageBody] = useState("")

  const readyAgents = useMemo(
    () => agents.filter((agent) => normalizeStatus(agent.connectionStatus) === "Ready").length,
    [agents],
  )

  const qrAgents = useMemo(
    () => agents.filter((agent) => normalizeStatus(agent.connectionStatus) === "WaitingQrCode").length,
    [agents],
  )

  const failedAgents = useMemo(
    () => agents.filter((agent) => normalizeStatus(agent.connectionStatus) === "Failed").length,
    [agents],
  )

  const healthyAgents = useMemo(() => agents.filter((agent) => agent.isProcessHealthy).length, [agents])

  const linkedAgent = agents[0] ?? null

  const hasLinkedAgent = agents.length > 0

  const hasValidPairingCode = isPairingCodeValid(pairingData)

  const downloadUrl = useMemo(() => buildDownloadUrl(installerInfo), [installerInfo])

  useQuickActionPopup(
    "whatsapp-pair-agent",
    () => {
      setIsPairingPopupOpen(true)
    },
    isOverviewLoaded && !hasLinkedAgent,
  )

  useQuickActionPopup(
    "whatsapp-test-message",
    () => {
      setIsTestPopupOpen(true)
    },
    isOverviewLoaded && hasLinkedAgent,
  )

  const load = async () => {
    const [nextAgents, nextInstallerInfo] = await Promise.all([getOverview(), getInstallerInfo()])

    setAgents(nextAgents)

    setInstallerInfo(nextInstallerInfo)

    setIsOverviewLoaded(true)
  }

  useEffect(() => {
    void load()
  }, [])

  const handleGeneratePairing = async () => {
    if (!agentName.trim()) {
      return
    }

    setPairingData(await generatePairingCode(agentName))

    await load()
  }

  const handleSendTest = async () => {
    if (!linkedAgent?.id || !toPhone.trim() || !messageBody.trim()) {
      return
    }

    await sendMessage({ agentId: linkedAgent.id, toPhone, body: messageBody })

    setMessageBody("")

    setIsTestPopupOpen(false)

    await load()
  }

  const handleDeleteAgent = async (agentId: number) => {
    await deleteAgent(agentId)

    setPairingData(null)

    await load()
  }

  const confirmDeleteAgent = (agent: IWhatsAppAgentOverviewDto) => {
    setNewConfirmation(
      t("label_whatsapp_automation_remove_agent_title"),
      t("label_whatsapp_automation_remove_agent_description", { name: agent.name }),
      async () => {
        await handleDeleteAgent(agent.id)
      },
    )
  }

  const resolveStatusLabel = (status: WhatsAppAgentConnectionStatus) => {
    switch (normalizeStatus(status)) {
      case "WaitingQrCode":
        return t("label_whatsapp_automation_status_waiting_qr")
      case "Ready":
        return t("label_whatsapp_automation_status_ready")
      case "Disconnected":
        return t("label_whatsapp_automation_status_disconnected")
      case "Failed":
        return t("label_whatsapp_automation_status_failed")
      default:
        return t("label_whatsapp_automation_status_not_started")
    }
  }

  const resolveStatusClassName = (status: WhatsAppAgentConnectionStatus) => {
    switch (normalizeStatus(status)) {
      case "Ready":
        return "border-emerald-200 bg-emerald-50 text-emerald-700"
      case "WaitingQrCode":
        return "border-sky-200 bg-sky-50 text-sky-700"
      case "Failed":
        return "border-red-200 bg-red-50 text-red-700"
      case "Disconnected":
        return "border-orange-200 bg-orange-50 text-orange-700"
      default:
        return "border-slate-200 bg-slate-50 text-slate-700"
    }
  }

  return (
    <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-4 md:p-2">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="p3">
          <div className="mb-3 mt-3 flex flex-row items-start justify-between p-2">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <FontAwesomeIcon icon={faWhatsapp} className="text-sm" />
              </span>
              {t("label_whatsapp_automation_title")}
            </h2>

            <PopupHelpButton
              variant="global"
              helpKey="pages.whatsapp-automation"
              title={t("label_whatsapp_automation_title")}
              description={t("label_whatsapp_automation_subtitle")}
              bullets={[
                t("label_whatsapp_automation_tip_download"),
                t("label_whatsapp_automation_tip_pair"),
                t("label_whatsapp_automation_tip_risk"),
              ]}
              steps={[
                t("label_whatsapp_automation_help_step_1"),
                t("label_whatsapp_automation_help_step_2"),
                t("label_whatsapp_automation_help_step_3"),
                t("label_whatsapp_automation_help_step_4"),
                t("label_whatsapp_automation_help_step_5"),
                t("label_whatsapp_automation_help_step_6"),
              ]}
            />
          </div>

          <div className="mt-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 shadow-sm">
            <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 sm:mt-0.5">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>

              <div>
                <p className="sm:mt-2">{t("label_whatsapp_automation_unofficial_warning_description")}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-bold text-amber-800">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    {t("label_whatsapp_automation_no_liability")}
                  </span>

                  <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-bold text-blue-700">
                    <FontAwesomeIcon icon={faFacebook} />
                    {t("label_whatsapp_automation_meta_notice")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {hasLinkedAgent && (
            <div className="mt-3 flex-col sm:flex-row flex items-center gap-3 rounded-lg border-2 border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 shadow-sm">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                <FontAwesomeIcon icon={faDesktop} />
              </span>

              <strong className="text-justify">{t("label_whatsapp_automation_single_agent_description")}</strong>
            </div>
          )}

          <div className="mb-3 mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title={t("label_whatsapp_automation_agents")}
              value={String(agents.length)}
              icon={faCircleNodes}
              color="#475569"
            />

            <MetricCard
              title={t("label_whatsapp_automation_daemon_health")}
              value={String(healthyAgents)}
              icon={faHeartPulse}
              color="#047857"
            />

            <MetricCard
              title={t("label_whatsapp_automation_ready")}
              value={String(readyAgents)}
              icon={faCircleCheck}
              color="#0369a1"
            />

            <MetricCard
              title={t("label_whatsapp_automation_failed")}
              value={String(failedAgents)}
              icon={faExclamationTriangle}
              color="#dc2626"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href={downloadUrl}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-500 to-teal-600 px-3 py-2 text-sm font-bold text-white shadow-md transition hover:brightness-105"
              title={t("label_whatsapp_automation_download_agent")}
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.whatsAppAutomationPage.link.whatsappAutomationDownloadAgent"
            >
              <ButtonIcon
                icon={faDownload}
                sizeNumber={34}
                bgColor="#047857"
                iconColor="white"
                descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.whatsAppAutomationPage.button.faDownload"
              />
              <span>{t("label_whatsapp_automation_download_agent")}</span>
            </a>

            <button
              onClick={() => setIsPairingPopupOpen(true)}
              disabled={hasLinkedAgent}
              title={hasLinkedAgent ? t("label_whatsapp_automation_single_agent_action_blocked") : undefined}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.whatsAppAutomationPage.button.whatsappAutomationSingleAgentActionBlocked"
            >
              <FontAwesomeIcon icon={faPlus} />
              {t("label_whatsapp_automation_open_pairing")}
            </button>

            <button
              onClick={() => {
                if (hasLinkedAgent) setIsTestPopupOpen(true)
              }}
              disabled={!hasLinkedAgent}
              title={!hasLinkedAgent ? t("label_whatsapp_automation_test_requires_agent") : undefined}
              className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-bold text-orange-700 shadow-sm transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-60"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.whatsAppAutomationPage.button.whatsappAutomationTestRequiresAgent"
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              {t("label_whatsapp_automation_open_test")}
            </button>

            <button
              onClick={() => void load()}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 shadow-sm transition hover:bg-sky-100"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.whatsAppAutomationPage.button.voidLoad"
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
              {t("label_print_automation_refresh")}
            </button>
          </div>

          {hasValidPairingCode && (
            <div className="mt-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 shadow-sm">
              <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-start sm:text-left">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 sm:mt-0.5">
                  <FontAwesomeIcon icon={faMobileScreenButton} />
                </span>

                <div className="w-full min-w-0">
                  <strong className="block text-slate-900">
                    {t("label_whatsapp_automation_pairing_code_visible_title")}
                  </strong>

                  <p className="mt-1 text-xs text-emerald-800">
                    {t("label_whatsapp_automation_pairing_code_visible_description")}
                  </p>

                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    <div className="rounded-lg border border-emerald-200 bg-white p-2 text-xs">
                      <strong className="block text-slate-700">{t("label_print_automation_agent_id_label")}</strong>

                      <span className="text-base font-bold text-emerald-800">{pairingData?.agentId}</span>
                    </div>

                    <div className="rounded-lg border border-emerald-200 bg-white p-2 text-xs">
                      <strong className="block text-slate-700">{t("label_print_automation_pairing_label")}</strong>

                      <span className="break-all text-base font-bold text-emerald-800">{pairingData?.pairingCode}</span>
                    </div>

                    <div className="rounded-lg border border-emerald-200 bg-white p-2 text-xs">
                      <strong className="block text-slate-700">{t("label_print_automation_expiration_label")}</strong>

                      <span className="font-bold text-emerald-800">{pairingData?.expiresAt?.toReadableDate()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
        <div className="mt-6 grid gap-3 px-2">
          <section>
            {agents.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-emerald-200 bg-white p-6 text-center text-sm text-slate-500">
                <FontAwesomeIcon icon={faWhatsapp} className="mb-2 text-2xl text-emerald-600" />

                <p>{t("label_whatsapp_automation_empty")}</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {agents.map((agent) => (
                  <article key={agent.id} className="rounded-lg border-2 border-emerald-200 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <strong className="max-w-full wrap-break-word text-base text-slate-800">{agent.name}</strong>

                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold ${
                            agent.isOnline
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-red-200 bg-red-50 text-red-700"
                          }`}
                        >
                          {agent.isOnline ? t("label_print_automation_online") : t("label_print_automation_offline")}
                        </span>

                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold ${resolveStatusClassName(
                            agent.connectionStatus,
                          )}`}
                        >
                          {resolveStatusLabel(agent.connectionStatus)}
                        </span>

                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold ${
                            agent.isProcessHealthy
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-red-200 bg-red-50 text-red-700"
                          }`}
                        >
                          {agent.isProcessHealthy
                            ? t("label_whatsapp_automation_daemon_running")
                            : t("label_whatsapp_automation_daemon_not_running")}
                        </span>
                      </div>

                      <ButtonIcon
                        icon={faTrash}
                        sizeNumber={34}
                        bgColor="#ef4444"
                        iconColor="white"
                        onClick={() => confirmDeleteAgent(agent)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.whatsAppAutomationPage.button.confirmDeleteAgentAgent"
                      />
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-900">
                        <strong>{t("label_print_automation_agent_id_label")}:</strong> {agent.id}
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-700">
                        {t("label_print_automation_machine", { machineId: agent.machineId })}
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-900">
                        <strong>{t("label_print_automation_version", { version: agent.version })}</strong>
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-700">
                        <strong>
                          {t("label_print_automation_last_seen", { date: agent.lastSeenAt?.toReadableDate() ?? "-" })}
                        </strong>
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-700">
                        <strong>{t("label_whatsapp_automation_last_heartbeat_age")}:</strong>{" "}
                        {formatHeartbeatAge(agent.secondsSinceLastSeen)}
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-700">
                        <strong>{t("label_whatsapp_automation_whatsapp_session")}:</strong>{" "}
                        {agent.isWhatsAppReady
                          ? t("label_whatsapp_automation_session_ready_short")
                          : t("label_whatsapp_automation_session_not_ready_short")}
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-700">
                        <strong>{t("label_whatsapp_automation_qr_updated_at")}:</strong>{" "}
                        {agent.qrCodeUpdatedAt?.toReadableDate() ?? "-"}
                      </div>

                      <div className="rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-700">
                        <strong>{t("label_whatsapp_automation_pairing_status")}:</strong>{" "}
                        {agent.hasPendingPairing
                          ? t("label_print_automation_pairing_pending")
                          : t("label_print_automation_pairing_active")}
                      </div>
                    </div>

                    <div className="mt-3 grid gap-3">
                      {agent.currentQrCodeDataUrl && (
                        <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                          <div className="mb-3 flex items-start gap-2 text-sm text-sky-900">
                            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-sky-700">
                              <FontAwesomeIcon icon={faMobileScreenButton} />
                            </span>

                            <div>
                              <strong className="block text-slate-900">
                                {t("label_whatsapp_automation_qr_needed_title")}
                              </strong>

                              <p className="mt-1">{t("label_whatsapp_automation_qr_description")}</p>
                            </div>
                          </div>

                          <img
                            src={agent.currentQrCodeDataUrl}
                            alt={t("label_whatsapp_automation_qr_alt")}
                            className="mx-auto h-40 w-40 rounded-lg border border-emerald-200 bg-white p-2"
                          />
                        </div>
                      )}

                      <div className="rounded-lg border border-slate-100 bg-white p-3">
                        <div className="grid gap-2 text-xs sm:grid-cols-2">
                          <span className="rounded-lg border border-slate-100 p-2">
                            <strong>{t("label_whatsapp_automation_phone")}:</strong>{" "}
                            {agent.connectedPhone || t("label_whatsapp_automation_no_phone")}
                          </span>

                          {agent.currentQrCodeDataUrl && (
                            <span className="rounded-lg border border-slate-100 p-2">
                              <strong>{t("label_whatsapp_automation_qr_agents")}:</strong> {qrAgents}
                            </span>
                          )}
                        </div>

                        <div
                          className={`mt-3 rounded-lg border p-3 text-xs font-semibold ${
                            agent.isProcessHealthy && agent.isWhatsAppReady
                              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                              : "border-amber-200 bg-amber-50 text-amber-900"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <FontAwesomeIcon
                              icon={
                                agent.isProcessHealthy && agent.isWhatsAppReady ? faHeartPulse : faExclamationTriangle
                              }
                              className="mt-0.5"
                            />
                            <span>{agent.healthDescription}</span>
                          </div>
                        </div>

                        {agent.lastError && (
                          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700">
                            {agent.lastError}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </Animate>

      <WhatsAppPairingPopup
        isOpen={isPairingPopupOpen}
        agentName={agentName}
        onClose={() => setIsPairingPopupOpen(false)}
        onChangeAgentName={setAgentName}
        onGeneratePairing={() => void handleGeneratePairing()}
      />

      <WhatsAppSendTestPopup
        isOpen={isTestPopupOpen}
        agent={linkedAgent}
        toPhone={toPhone}
        messageBody={messageBody}
        onClose={() => setIsTestPopupOpen(false)}
        onChangeToPhone={setToPhone}
        onChangeMessageBody={setMessageBody}
        onSendTest={() => void handleSendTest()}
      />
    </div>
  )
}
